import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { logger } from '@/services/logger';

/**
 * Props for AppErrorBoundary
 */
interface AppErrorBoundaryProps {
  children: ReactNode;
}

/**
 * State for AppErrorBoundary
 */
interface AppErrorBoundaryState {
  hasError: boolean;
}

/**
 * Root error boundary. Reports render errors to Application Insights with the component stack,
 * so they reach the error triage feeder, and shows a recovery screen instead of a blank page.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  /**
   * Switches to the fallback UI after a descendant throws during rendering
   */
  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  /**
   * Reports the render error
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error(error, { context: 'Render', componentStack: errorInfo.componentStack });
  }

  /**
   * Renders the children, or the fallback UI after a render error
   */
  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Something went wrong</CardTitle>
            <CardDescription>
              An unexpected error occurred and has been reported. Reload the page to continue.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={() => window.location.reload()} className="w-full">
              Reload
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
}
